import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Phone, ChevronRight, Sparkles, Building2 } from "lucide-react";
import { BRAND } from "@/config/brand";
import { DFW_CITIES } from "@/config/cities";
import { DFW_COUNTIES } from "@/config/dfw-master";
import PlugitAIChat from "@/pages/PlugitAIChat";

const C = BRAND.colors;

export default function CitiesIndex() {
  useEffect(() => {
    document.title = "AI Website Design & Sales Agents Across DFW | PlugitAI";
    const set = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.content = content;
    };
    set("description", "PlugitAI builds AI-powered websites and sales agents for small businesses across the Dallas–Fort Worth metroplex. Find your city and lock in your project with $299.99.");
    set("robots", "index,follow");
  }, []);

  return (
    <div className="min-h-screen w-full" style={{ background: C.ink, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}
         data-testid="cities-index">
      <TopNav />
      <section className="relative z-10 pt-28 md:pt-40 pb-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <nav className="text-xs uppercase tracking-[0.18em] flex items-center gap-2 mb-8" style={{ color: C.textFade }}>
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight size={12} />
            <span style={{ color: C.text }}>Texas</span>
          </nav>

          <div className="max-w-[820px] mb-14">
            <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: C.greenGlow }}>PlugitAI · Dallas–Fort Worth</div>
            <h1 className="font-black tracking-[-0.02em] leading-[1.02]" style={{ fontSize: "clamp(2.2rem, 4.4vw, 4rem)" }}>
              Serving every{" "}
              <span style={{
                backgroundImage: `linear-gradient(135deg, ${C.blueGlow} 0%, ${C.greenGlow} 100%)`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>DFW city</span>.
            </h1>
            <p className="mt-4 text-lg" style={{ color: C.textDim }}>
              AI-powered websites, sales agents, and 24/7 lead-response systems for small
              businesses across the Dallas–Fort Worth metroplex. Click your city to see local examples.
            </p>
          </div>

          {/* County authority rail */}
          <div className="mb-16" data-testid="counties-rail">
            <div className="text-xs uppercase tracking-[0.22em] mb-4" style={{ color: C.blueGlow }}>
              Browse by county · 12 authority pages
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {DFW_COUNTIES.map((cty) => (
                <Link key={cty.slug} to={`/tx/county/${cty.slug}`}
                      className="group rounded-xl p-4 border transition-all hover:-translate-y-1"
                      style={{
                        borderColor: `${cty.color}44`,
                        background: `linear-gradient(160deg, ${cty.color}14 0%, transparent 55%), rgba(17,24,39,0.55)`,
                        backdropFilter: "blur(10px)",
                      }}
                      data-testid={`county-card-${cty.slug}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Building2 size={12} style={{ color: cty.color }} />
                    <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: cty.color }}>
                      {cty.population}
                    </div>
                  </div>
                  <div className="font-black text-white text-sm md:text-base leading-tight">
                    {cty.name}
                  </div>
                  <div className="text-[10px] mt-1" style={{ color: C.textFade }}>
                    Seat: {cty.seat}
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="text-xs uppercase tracking-[0.22em] mb-4" style={{ color: C.greenGlow }}>
            All {DFW_CITIES.length} cities we serve
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {DFW_CITIES.map((c) => (
              <Link key={c.slug} to={`/tx/${c.slug}`}
                    className="group rounded-2xl p-5 md:p-6 border transition-all hover:-translate-y-1"
                    style={{
                      borderColor: C.line,
                      background: "rgba(17,24,39,0.55)",
                      backdropFilter: "blur(10px)",
                      boxShadow: `0 30px 80px -30px ${C.blueBright}44`,
                    }}
                    data-testid={`city-card-${c.slug}`}>
                <div className="flex items-start justify-between mb-3">
                  <MapPin size={18} style={{ color: C.greenGlow }} />
                  <span className="text-[9px] uppercase tracking-[0.22em]" style={{ color: C.textFade }}>{c.population}</span>
                </div>
                <div className="font-bold text-white text-lg md:text-xl leading-tight">{c.name}, TX</div>
                <div className="mt-1 text-xs" style={{ color: C.textFade }}>{c.county}</div>
                <div className="mt-4 flex flex-wrap gap-1">
                  {c.industries.slice(0, 3).map((ind) => (
                    <span key={ind} className="text-[10px] px-2 py-0.5 rounded-full"
                          style={{ background: `${C.blueBright}18`, color: C.blueGlow, border: `1px solid ${C.blueBright}44` }}>
                      {ind}
                    </span>
                  ))}
                </div>
                <div className="mt-5 flex items-center gap-1 text-xs" style={{ color: C.blueGlow }}>
                  Visit {c.name} page <ArrowUpRight size={12} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <PlugitAIChat />
    </div>
  );
}

function TopNav() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50"
            style={{ background: "rgba(10,15,30,0.72)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${C.line}` }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-10 md:h-12 w-auto" />
          <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark" style={{ borderColor: C.line }}>
            Powered by {BRAND.engineName}
          </span>
        </Link>
        <a href="tel:+12148155966"
           className="inline-flex items-center gap-2 px-4 md:px-5 h-10 md:h-11 rounded-full font-semibold text-sm"
           style={{ background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`, color: "white" }}>
          <Phone size={14} /> (214) 815-5966
        </a>
      </div>
    </header>
  );
}
